import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 bg: '#0F1117', surface: '#1A1D27', surface2: '#22263A',
 border: 'rgba(255,255,255,.08)', border2: 'rgba(255,255,255,.14)',
 text: '#F0F2FF', text2: '#A0A8C0', text3: '#606880',
 accent: '#3AAF58', blue: '#2563A8', blueLight: '#3B82F6',
 red: '#E05050', redBg: 'rgba(224,80,80,.10)', redBorder: 'rgba(224,80,80,.25)',
 amber: '#D4840F', amberBg: 'rgba(212,132,15,.10)', amberBorder: 'rgba(212,132,15,.25)',
 green: '#3AAF58', greenBg: 'rgba(58,175,88,.10)', greenBorder: 'rgba(58,175,88,.25)',
 purple: '#8060C0', teal: '#1A7A7A',
};

const STATUS_CONFIG = {
 'Vacant Clean': { color: '#3AAF58', bg: 'rgba(58,175,88,.15)', label: 'Vacant Clean' },
 'Vacant Dirty': { color: '#D4840F', bg: 'rgba(212,132,15,.15)', label: 'Vacant Dirty' },
 'Occupied': { color: '#2563A8', bg: 'rgba(37,99,168,.15)', label: 'Occupied' },
 'Maintenance': { color: '#E05050', bg: 'rgba(224,80,80,.15)', label: 'Maintenance' },
 'Blocked': { color: '#606880', bg: 'rgba(96,104,128,.15)', label: 'Blocked' },
 'Check-out': { color: '#8060C0', bg: 'rgba(128,96,192,.15)', label: 'Check-out' },
};

const ROOM_STATUSES = ['Vacant Clean', 'Vacant Dirty', 'Occupied', 'Maintenance', 'Blocked'];

const inr = n => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = d => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const inputStyle = {
 width: '100%', padding: '10px 12px', background: '#22263A',
 border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, color: '#F0F2FF',
 fontSize: 14, boxSizing: 'border-box', outline: 'none',
};
const selectStyle = { ...inputStyle, cursor: 'pointer' };

function Modal({ title, onClose, children, maxWidth = 520 }) {
 return (
 <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
 <div style={{ background: '#1A1D27', border: '1px solid rgba(255,255,255,.14)', borderRadius: 16, width: '100%', maxWidth, maxHeight: '90vh', overflow: 'auto' }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
 <div style={{ fontWeight: 700, fontSize: 15, color: '#F0F2FF' }}>{title}</div>
 <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#606880', fontSize: 20, cursor: 'pointer' }}></button>
 </div>
 <div style={{ padding: 22 }}>{children}</div>
 </div>
 </div>
 );
}

function Field({ label, children }) {
 return (
 <div style={{ marginBottom: 14 }}>
 <label style={{ display: 'block', fontSize: 12, color: '#A0A8C0', marginBottom: 5, fontWeight: 600 }}>{label}</label>
 {children}
 </div>
 );
}

// ── ROOM DETAIL MODAL ─────────────────────────────────────────────────────────
function RoomDetailModal({ room, onClose, onUpdate }) {
 const [newStatus, setNewStatus] = useState(room.status);
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState('');
 const cfg = STATUS_CONFIG[room.status] || STATUS_CONFIG['Vacant Clean'];

 const handleStatusUpdate = async () => {
 setSaving(true); setError('');
 try {
 const res = await fetch(`${API}/rooms/${room.id}/status`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ status: newStatus }),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error || 'Failed to update');
 onUpdate(); onClose();
 } catch (err) { setError(err.message); }
 finally { setSaving(false); }
 };

 const handleCheckout = async () => {
 setSaving(true); setError('');
 try {
 const res = await fetch(`${API}/rooms/${room.id}/checkout`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ reservation_id: room.reservation_id }),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error || 'Failed to checkout');
 onUpdate(); onClose();
 } catch (err) { setError(err.message); }
 finally { setSaving(false); }
 };

 return (
 <Modal title={`Room ${room.room_number} — ${room.type_name || 'Standard'}`} onClose={onClose}>
 {error && <div style={{ padding: '10px 14px', background: C.redBg, border: `1px solid ${C.redBorder}`, borderRadius: 8, color: C.red, fontSize: 13, marginBottom: 14 }}>{error}</div>}

 {/* Room Info */}
 <div style={{ background: C.surface2, borderRadius: 12, padding: 16, marginBottom: 18 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
 <div>
 <div style={{ fontSize: 28, fontWeight: 900, color: C.text }}>Room {room.room_number}</div>
 <div style={{ fontSize: 13, color: C.text2 }}>{room.type_name} · Floor {room.floor}</div>
 </div>
 <div style={{ padding: '6px 14px', borderRadius: 99, background: cfg.bg, color: cfg.color, fontSize: 13, fontWeight: 700 }}>
 {cfg.icon} {room.status}
 </div>
 </div>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
 <div style={{ fontSize: 12, color: C.text2 }}>Base Tariff: <strong style={{ color: C.accent }}>{inr(room.base_tariff)}/night</strong></div>
 <div style={{ fontSize: 12, color: C.text2 }}>Occupancy: <strong style={{ color: C.text }}>{room.occupancy} pax</strong></div>
 <div style={{ fontSize: 12, color: C.text2 }}>HK Status: <strong style={{ color: C.text }}>{room.housekeeping_status || '—'}</strong></div>
 <div style={{ fontSize: 12, color: C.text2 }}>Maintenance: <strong style={{ color: C.text }}>{room.maintenance_status || 'None'}</strong></div>
 </div>
 </div>

 {/* Current Guest */}
 {room.status === 'Occupied' && (
 <div style={{ background: `${C.blue}18`, border: `1px solid ${C.blue}33`, borderRadius: 10, padding: 14, marginBottom: 18 }}>
 <div style={{ fontSize: 12, color: C.text2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Current Guest</div>
 <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>{room.guest_name || 'Guest'}</div>
 <div style={{ fontSize: 12, color: C.text2, marginBottom: 4 }}> {room.guest_phone || '—'}</div>
 <div style={{ fontSize: 12, color: C.text2, marginBottom: 4 }}>Check-in: <strong style={{ color: C.text }}>{fmtDateTime(room.checkin_time)}</strong></div>
 <div style={{ fontSize: 12, color: C.text2, marginBottom: 12 }}>Check-out: <strong style={{ color: C.text }}>{fmtDateTime(room.checkout_time)}</strong></div>
 <button onClick={handleCheckout} disabled={saving} style={{ width: '100%', padding: '10px', background: C.red, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, opacity: saving ? .6 : 1 }}>
 Check Out Guest
 </button>
 </div>
 )}

 {/* Update Status */}
 <Field label="Update Room Status">
 <select value={newStatus} onChange={e => setNewStatus(e.target.value)} style={selectStyle}>
 {ROOM_STATUSES.map(s => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 </Field>

 <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
 <button onClick={onClose} style={{ padding: '10px 18px', background: 'none', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, color: C.text2, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
 <button onClick={handleStatusUpdate} disabled={saving || newStatus === room.status} style={{ padding: '10px 22px', background: C.accent, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: (saving || newStatus === room.status) ? .5 : 1 }}>
 {saving ? 'Saving…' : 'Update Status'}
 </button>
 </div>
 </Modal>
 );
}

// ── CHECK-IN MODAL ────────────────────────────────────────────────────────────
function CheckInModal({ room, onClose, onUpdate }) {
 const [bookings, setBookings] = useState([]);
 const [form, setForm] = useState({
 booking_id: '',
 guest_name: '', guest_phone: '',
 checkin_time: new Date().toISOString().slice(0, 16),
 checkout_time: '',
 rate: room.base_tariff || 0,
 });
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState('');
 const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

 useEffect(() => {
 fetch(`${API}/bookings?status=Confirmed`)
 .then(r => r.json())
 .then(d => setBookings(Array.isArray(d) ? d : []))
 .catch(() => {});
 }, []);

 const handleCheckin = async () => {
 if (!form.guest_name && !form.booking_id) { setError('Enter guest name or select a booking'); return; }
 setSaving(true); setError('');
 try {
 const res = await fetch(`${API}/rooms/${room.id}/checkin`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(form),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error || 'Failed to check in');
 onUpdate(); onClose();
 } catch (err) { setError(err.message); }
 finally { setSaving(false); }
 };

 return (
 <Modal title={`Check-in — Room ${room.room_number}`} onClose={onClose}>
 {error && <div style={{ padding: '10px 14px', background: C.redBg, border: `1px solid ${C.redBorder}`, borderRadius: 8, color: C.red, fontSize: 13, marginBottom: 14 }}>{error}</div>}

 <div style={{ background: C.surface2, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13 }}>
 <span style={{ color: C.text2 }}>Room </span><strong style={{ color: C.accent }}>{room.room_number}</strong>
 <span style={{ color: C.text2 }}> · {room.type_name} · </span>
 <strong style={{ color: C.accent }}>{inr(room.base_tariff)}/night</strong>
 </div>

 <Field label="Link to Existing Booking (optional)">
 <select value={form.booking_id} onChange={e => {
 const b = bookings.find(x => x.id === parseInt(e.target.value));
 set('booking_id', e.target.value);
 if (b) { set('guest_name', b.guest_name || ''); set('guest_phone', b.guest_phone || ''); }
 }} style={selectStyle}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="">— Walk-in / No booking —</option>
 {bookings.map(b => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={b.id} value={b.id}>{b.booking_code} — {b.guest_name}</option>)}
 </select>
 </Field>

 <Field label="Guest Name *">
 <input type="text" value={form.guest_name} onChange={e => set('guest_name', e.target.value)} placeholder="Full name" style={inputStyle} />
 </Field>
 <Field label="Guest Phone">
 <input type="text" value={form.guest_phone} onChange={e => set('guest_phone', e.target.value)} placeholder="Mobile number" style={inputStyle} />
 </Field>

 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
 <Field label="Check-in Time">
 <input type="datetime-local" value={form.checkin_time} onChange={e => set('checkin_time', e.target.value)} style={inputStyle} />
 </Field>
 <Field label="Check-out Time">
 <input type="datetime-local" value={form.checkout_time} onChange={e => set('checkout_time', e.target.value)} style={inputStyle} />
 </Field>
 </div>

 <Field label="Rate per Night (₹)">
 <input type="number" value={form.rate} onChange={e => set('rate', e.target.value)} style={inputStyle} />
 </Field>

 <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
 <button onClick={onClose} style={{ padding: '10px 18px', background: 'none', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, color: C.text2, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
 <button onClick={handleCheckin} disabled={saving} style={{ padding: '10px 22px', background: C.blue, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, opacity: saving ? .6 : 1 }}>
 {saving ? 'Checking in…' : ' Check In'}
 </button>
 </div>
 </Modal>
 );
}

// ── ROOM CARD ─────────────────────────────────────────────────────────────────
function RoomCard({ room, onSelect, onCheckin }) {
 const cfg = STATUS_CONFIG[room.status] || { color: '#606880', bg: 'rgba(96,104,128,.15)', };
 const isOccupied = room.status === 'Occupied';
 const isVacant = room.status === 'Vacant Clean' || room.status === 'Vacant Dirty';

 return (
 <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, cursor: 'pointer', transition: 'all .2s', position: 'relative' }}
 onClick={() => onSelect(room)}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
 <div style={{ fontSize: 22, fontWeight: 900, color: C.text }}>
 {room.room_number}
 </div>
 <div style={{ fontSize: 18 }}>{cfg.icon}</div>
 </div>
 <div style={{ fontSize: 11, color: C.text2, marginBottom: 6 }}>{room.type_name || 'Standard'}</div>
 <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 99, background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700, marginBottom: 10 }}>
 {room.status}
 </div>

 {isOccupied && room.guest_name && (
 <div style={{ fontSize: 11, color: C.text2, marginBottom: 6 }}>
 {room.guest_name}
 {room.checkout_time && <div style={{ color: C.amber }}>Out: {fmtDate(room.checkout_time)}</div>}
 </div>
 )}

 <div style={{ fontSize: 11, color: C.text3, marginBottom: 10 }}>{inr(room.base_tariff)}/night · {room.occupancy} pax</div>

 {isVacant && (
 <button onClick={e => { e.stopPropagation(); onCheckin(room); }}
 style={{ width: '100%', padding: '7px', background: C.blue, border: 'none', borderRadius: 7, color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
 Check In
 </button>
 )}
 </div>
 );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function RoomPMS() {
 const [rooms, setRooms] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');
 const [filterStatus, setFilterStatus] = useState('All');
 const [filterFloor, setFilterFloor] = useState('All');
 const [selectedRoom, setSelectedRoom] = useState(null);
 const [checkinRoom, setCheckinRoom] = useState(null);

 const loadRooms = useCallback(async () => {
 setLoading(true); setError('');
 try {
 const res = await fetch(`${API}/rooms`);
 const data = await res.json();
 if (!res.ok) throw new Error(data.error || 'Failed to load rooms');
 setRooms(Array.isArray(data) ? data : []);
 } catch (err) { setError(err.message); }
 finally { setLoading(false); }
 }, []);

 useEffect(() => { loadRooms(); }, [loadRooms]);

 // Stats
 const total = rooms.length;
 const occupied = rooms.filter(r => r.status === 'Occupied').length;
 const vacantClean = rooms.filter(r => r.status === 'Vacant Clean').length;
 const vacantDirty = rooms.filter(r => r.status === 'Vacant Dirty').length;
 const maintenance = rooms.filter(r => r.status === 'Maintenance').length;
 const occPct = total > 0 ? Math.round(occupied / total * 100) : 0;

 // Filters
 const floors = ['All', ...Array.from(new Set(rooms.map(r => r.floor))).sort()];
 let filtered = rooms;
 if (filterStatus !== 'All') filtered = filtered.filter(r => r.status === filterStatus);
 if (filterFloor !== 'All') filtered = filtered.filter(r => r.floor === parseInt(filterFloor));

 return (
 <div style={{ padding: 24, background: C.bg, minHeight: '100vh', color: C.text, fontFamily: 'Inter, sans-serif' }}>

 {/* Header */}
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
 <div>
 <div style={{ fontSize: 22, fontWeight: 800 }}> Room PMS</div>
 <div style={{ fontSize: 13, color: C.text2, marginTop: 2 }}>{total} rooms · {occPct}% occupied</div>
 </div>
 <button onClick={loadRooms} style={{ padding: '10px 18px', background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 10, color: C.text2, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}> Refresh</button>
 </div>

 {/* Stats */}
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 24 }}>
 {[
 { label: 'Total Rooms', value: total, color: C.text, bg: C.surface },
 { label: 'Occupied', value: occupied, color: '#2563A8', bg: 'rgba(37,99,168,.1)' },
 { label: 'Vacant Clean', value: vacantClean, color: '#3AAF58', bg: 'rgba(58,175,88,.1)' },
 { label: 'Vacant Dirty', value: vacantDirty, color: '#D4840F', bg: 'rgba(212,132,15,.1)' },
 { label: 'Maintenance', value: maintenance, color: '#E05050', bg: 'rgba(224,80,80,.1)' },
 ].map(s => (
 <div key={s.label} style={{ background: s.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
 <div style={{ fontSize: 26, fontWeight: 900, color: s.color }}>{s.value}</div>
 <div style={{ fontSize: 11, color: C.text2, marginTop: 2 }}>{s.label}</div>
 </div>
 ))}
 </div>

 {/* Occupancy Bar */}
 <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.text2, marginBottom: 8 }}>
 <span>Occupancy Rate</span>
 <strong style={{ color: C.text }}>{occPct}%</strong>
 </div>
 <div style={{ height: 8, background: 'rgba(255,255,255,.06)', borderRadius: 99 }}>
 <div style={{ height: 8, width: `${occPct}%`, background: `linear-gradient(90deg, #2563A8, #3AAF58)`, borderRadius: 99, transition: 'width .5s' }} />
 </div>
 </div>

 {/* Filters */}
 <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
 <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...selectStyle, width: 180 }}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="All">All Status</option>
 {ROOM_STATUSES.map(s => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 <select value={filterFloor} onChange={e => setFilterFloor(e.target.value)} style={{ ...selectStyle, width: 140 }}>
 {floors.map(f => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={f} value={f}>{f === 'All' ? 'All Floors' : `Floor ${f}`}</option>)}
 </select>
 <div style={{ fontSize: 13, color: C.text2, padding: '10px 0' }}>{filtered.length} rooms shown</div>
 </div>

 {error && <div style={{ padding: '12px 16px', background: C.redBg, border: `1px solid ${C.redBorder}`, borderRadius: 10, color: C.red, marginBottom: 16, fontSize: 13 }}> {error}</div>}
 {loading && <div style={{ textAlign: 'center', padding: 60, color: C.text2 }}>Loading rooms…</div>}

 {/* Legend */}
 {!loading && (
 <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
 {Object.entries(STATUS_CONFIG).map(([status, cfg]) => (
 <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text2 }}>
 <span style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.color, display: 'inline-block' }} />
 {cfg.icon} {status}
 </div>
 ))}
 </div>
 )}

 {/* Room Grid */}
 {!loading && (
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14 }}>
 {filtered.map(room => (
 <RoomCard key={room.id} room={room} onSelect={setSelectedRoom} onCheckin={setCheckinRoom} />
 ))}
 </div>
 )}

 {/* Modals */}
 {selectedRoom && (
 <RoomDetailModal room={selectedRoom} onClose={() => setSelectedRoom(null)} onUpdate={loadRooms} />
 )}
 {checkinRoom && (
 <CheckInModal room={checkinRoom} onClose={() => setCheckinRoom(null)} onUpdate={loadRooms} />
 )}
 </div>
 );
}
